import React, { ReactNode, useMemo } from 'react';
import { ConditionalMobileDownloadOverlay } from 'App/MobileDownloadOverlay/ConditionalMobileDownloadOverlay';
import { matchPath, useLocation } from 'react-router';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { RouteConfig } from 'App/routes';
import { PendingMessageProvider } from 'Assistant/PendingMessage';
import LocalAuthStatus from '../App/Auth/LocalAuthStatus';
import AuthenticatedPage from '../Navigation/AuthenticatedPage';
import UnauthenticatedPage from '../Navigation/UnauthenticatedPage';

type PageWithNavigationProps = {
  routeConfigs: RouteConfig[];
  children?: ReactNode;
};

const getRouteConfigForPath = (
  pathname: string,
  routes: RouteConfig[],
): RouteConfig | undefined => {
  for (const route of routes) {
    const paths = Array.isArray(route.path) ? route.path : [route.path];
    for (const path of paths) {
      if (path && matchPath(pathname, { path, exact: route.exact !== false })) {
        return route;
      }
    }
  }

  return undefined;
};

const PageWithNavigation = ({
  children,
  routeConfigs,
}: PageWithNavigationProps) => {
  const { state: locationState, pathname } = useLocation();
  const isFirstLogin =
    history.state?.state?.isFirstLogin || locationState?.isFirstLogin;

  const isWelcomeModalEnabled = useFeatureFlag(
    FEATURE_FLAGS.WELCOME_MODAL_ENABLED,
  );

  const shouldShowMobileDownloadOverlayForFirstLogin =
    useFeatureFlag(
      FEATURE_FLAGS.ENTERPRISE_READINESS_SHOW_MOBILE_DOWNLOAD_ON_FIRST_LOGIN,
    ) && isFirstLogin;

  // The welcome modal owns onboarding while it is enabled. Both interrupt the
  // same moment, and the download prompt covered members part-way through it —
  // on any viewport, so this suppresses rather than flipping the override.
  //
  // Not scoped to the homepage: the modal's call to action navigates away from
  // it, so a route check would show the prompt the instant a member acted on
  // the modal. Eligibility now rests on `welcomeStatus`, which this component
  // cannot read without running the modal's queries on every page, so the flag
  // stands in for it until the overlay's longer-term place is decided.
  const suppressMobileDownloadOverlay = Boolean(isWelcomeModalEnabled);

  const routeConfig = useMemo(
    () => getRouteConfigForPath(pathname, routeConfigs),
    [pathname, routeConfigs],
  );

  return (
    <LocalAuthStatus>
      {({ state, isAuthenticated }) => (
        <>
          {state === 'READY' && isAuthenticated && (
            <PendingMessageProvider>
              <AuthenticatedPage layoutConfig={routeConfig?.layout}>
                {children}
              </AuthenticatedPage>
            </PendingMessageProvider>
          )}
          {!isAuthenticated && (
            <UnauthenticatedPage>{children}</UnauthenticatedPage>
          )}
          <ConditionalMobileDownloadOverlay
            showOverlayOverride={shouldShowMobileDownloadOverlayForFirstLogin}
            suppressed={suppressMobileDownloadOverlay}
          />
        </>
      )}
    </LocalAuthStatus>
  );
};

export default PageWithNavigation;
